import React from 'react';
import { Company, CompanyFile } from '@hum/types';
import { createUrlFromTemplate } from '@hum/icm-app/src/state';
import { Detail } from '@hum/legacy-ui';
import { Routes } from '@hum/icm-app/src/state/routes';
import { formatDate } from '@hum/icm-app/src/utils';
import { useSession } from '@hum/api/src/Session/useSession';
import { getCompanyFiles } from '@hum/api/src/domains/company/queries';
import { useApiMutation } from '@hum/api/src/hooks/useApiMutation';
import { useApiQuery } from '@hum/api/src/hooks/useApiQuery';
import { downloadCompanyFile } from '@hum/api/src/domains/company/mutations';

export const AdminSidebar = ({ company }: { company: Company }) => {
  const { user } = useSession();
  const { data: companyFiles } = useApiQuery(getCompanyFiles);
  const mndaFile = companyFiles?.find(
    (file: CompanyFile) => file.documentType === 'docusign_mnda'
  );
  const { mutate: downloadMnda } = useApiMutation(downloadCompanyFile, {
    onSuccess: (data) => {
      window.open(data.downloadUrl, '_blank');
    },
  });

  const profileV2Uri =
    //eslint-disable-next-line
    process.env.V2_BASE_URL +
    createUrlFromTemplate(`${Routes.COMPANY_PROFILE_V2}`, {
      companyId: company.id.toString(),
    });

  const financeUIUri =
    //eslint-disable-next-line
    process.env.V2_BASE_URL +
    createUrlFromTemplate(`${Routes.COMPANY_FINANCE_UI}`, {
      companyId: company.uuid.toString(),
    });

  const investorMemoUri =
    //eslint-disable-next-line
    process.env.V2_BASE_URL +
    createUrlFromTemplate(`${Routes.COMPANY_INVESTOR_MEMO}`, {
      companyId: company.uuid.toString(),
    });

  return (
    <div className="border-l-1 p-7">
      {user?.isAdmin && (
        <>
          <Detail
            title="Data Sharing"
            description={
              <a
                href={`/legacy${Routes.COMPANY_ACTIVATION_CONNECTORS_ACCOUNTING}/?company_id=${company.uuid}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="admin-page:company-sidebar:open-connectors-link"
              >
                Open →
              </a>
            }
            className="pb-7"
          />
          <Detail
            title="Profile"
            description={
              <a
                href={profileV2Uri}
                rel="noopener noreferrer"
                data-testid="admin-page:company-sidebar:profile-link"
              >
                Open →
              </a>
            }
            className="pb-7"
          />
          <Detail
            title="Investor Memo"
            description={
              <a
                href={investorMemoUri}
                rel="noopener noreferrer"
                data-testid="admin-page:company-sidebar:investor-memo-link"
              >
                Open →
              </a>
            }
            className="pb-7"
          />
          <Detail
            title="Insights"
            description={
              <a
                href={createUrlFromTemplate(
                  `/legacy${Routes.ADMIN_COMPANY_ANALYTICS}`,
                  { companyId: company.uuid }
                )}
                rel="noopener noreferrer"
                data-testid="admin-page:manage-insights:privacy-link"
              >
                Manage Insights →
              </a>
            }
            className="pb-7"
          />
          <Detail
            title="MNDA"
            description={
              mndaFile ? (
                <button
                  data-testid="admin-page:company-sidebar:privacy-link"
                  onClick={() =>
                    downloadMnda({
                      companyId: company.uuid,
                      fileId: mndaFile?.id,
                    })
                  }
                >
                  Download MNDA ↓
                </button>
              ) : (
                <span>No MNDA found</span>
              )
            }
            className="pb-7"
          />
          <Detail
            title="Labeling UI"
            description={
              <a
                href={`/legacy${Routes.LABEL_MAPPER}?company_id=${company.id}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="admin-page:company-sidebar:privacy-link"
              >
                Labeling UI →
              </a>
            }
            className="pb-7"
          />
          <Detail
            title="Finance UI"
            description={
              <a
                href={financeUIUri}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="admin-page:company-sidebar:finance-ui-link"
              >
                Finance UI →
              </a>
            }
            className="pb-7"
          />
        </>
      )}

      <Detail
        title="Created"
        description={formatDate(company.createdAt)}
        className="pb-7"
      />
      <Detail title="Updated" description={formatDate(company.updatedAt)} />
    </div>
  );
};
